import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "../lib/api";
import { LoadingState, ErrorState } from "./Loading";

export interface CoachPlan {
  id: string;
  name: string;
  description: string;
  priceCents: number;
  additionalAthleteCents: number;
  archived: boolean;
  sortOrder: number;
  /** A family has to ask before moving to this plan (27 Sep debug notes item 12). Absent reads as no. */
  requiresApproval?: boolean;
}

interface PlansResponse {
  plans: CoachPlan[];
  siblingDiscountPercent: number;
  currency: string;
}

const PLANS_KEY = ["billing", "plans"] as const;

export function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function toCents(dollars: string): number {
  return Math.round(Number(dollars) * 100);
}

// Approval on by default (decided 5 Oct 2026): a family switching on their own is the coach's choice to make.
const BLANK = { id: "", name: "", description: "", price: "", requiresApproval: true };

/**
 * The coach's subscription catalogue (finding #15).
 *
 * What a family paid was a Stripe Price id in the deploy config, then a fixed pair of tiers in the app.
 * Neither was the coach's to change, and neither could say what a family was actually buying -- which is
 * the thing they are choosing between. A plan here is a name, a description of what is in it, and a
 * price, and the family picks from the list.
 *
 * Two things are said out loud rather than left to be discovered, because both surprise people: a price
 * change applies to families who sign up after it, and the sibling discount applies from the second
 * athlete onwards rather than to the whole bill.
 */
export function PricingSettings() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: PLANS_KEY, queryFn: () => apiGet<PlansResponse>("/billing/plans") });

  const [draft, setDraft] = useState(BLANK);
  const [discount, setDiscount] = useState<string | null>(null);

  const savePlan = useMutation({
    mutationFn: (body: { id?: string; name: string; description: string; priceCents: number; requiresApproval?: boolean }) =>
      apiPost("/billing/plans", body),
    onSuccess: async () => {
      setDraft(BLANK);
      await queryClient.invalidateQueries({ queryKey: PLANS_KEY });
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiPost(`/billing/plans/${id}/${archived ? "restore" : "archive"}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLANS_KEY }),
  });

  const saveDiscount = useMutation({
    mutationFn: (percent: number) => apiPut("/billing/sibling-discount", { percent }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLANS_KEY }),
  });

  if (query.isPending) return <LoadingState label="Loading your plans…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const { plans, siblingDiscountPercent } = query.data;
  const discountValue = discount ?? String(siblingDiscountPercent);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    savePlan.mutate({
      ...(draft.id ? { id: draft.id } : {}),
      name: draft.name.trim(),
      description: draft.description.trim(),
      priceCents: toCents(draft.price || "0"),
      // Sent when off, or when editing -- turning it off has to reach the server, and a new plan without
      // it needs approval, which is the server's default too.
      ...(!draft.requiresApproval || draft.id ? { requiresApproval: draft.requiresApproval } : {}),
    });
  }

  return (
    <>
      <h2>Subscriptions</h2>
      <p className="form-hint">
        The plans families can choose between, and what each one includes. Changing a price sets it for
        families who sign up from then on — everybody already subscribed stays on the price they signed
        up at until you move them.
      </p>

      {savePlan.isError && <ErrorState error={savePlan.error} />}
      {archive.isError && <ErrorState error={archive.error} />}

      {plans.length === 0 ? (
        <p className="empty-state">No plans yet. Write your first one below — families can&rsquo;t sign up without one.</p>
      ) : (
        <ul className="plan-catalogue">
          {plans.map((plan) => (
            <li key={plan.id} className={plan.archived ? "plan-tile archived" : "plan-tile"}>
              <div className="plan-tile-head">
                <span className="client-name">{plan.name}</span>
                <span className="badge">{money(plan.priceCents)}/mo</span>
                {plan.archived && <span className="badge badge-warn">Archived</span>}
                {plan.requiresApproval && <span className="badge">Needs your approval</span>}
              </div>
              <p className="plan-description">{plan.description}</p>
              <p className="form-hint">
                Each additional athlete in the same family: {money(plan.additionalAthleteCents)}/mo
              </p>
              <div className="detail-actions">
                <button
                  type="button"
                  className="chip"
                  onClick={() =>
                    setDraft({
                      id: plan.id,
                      name: plan.name,
                      description: plan.description,
                      price: (plan.priceCents / 100).toFixed(2),
                      requiresApproval: plan.requiresApproval ?? false,
                    })
                  }
                >
                  Edit
                </button>
                {/* Archive, not delete: families are subscribed to these, and a subscription pointing at
                    a plan that no longer exists has nothing to name, price, or move them off. */}
                <button
                  type="button"
                  className="chip"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate({ id: plan.id, archived: plan.archived })}
                >
                  {plan.archived ? "Bring back" : "Archive"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form className="plan-form" onSubmit={handleSubmit}>
        <p className="form-label">{draft.id ? "Edit plan" : "Add a plan"}</p>

        <label htmlFor="plan-name">Name</label>
        <input id="plan-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />

        <label htmlFor="plan-description">What&rsquo;s included</label>
        <textarea
          id="plan-description"
          rows={3}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="A new plan every week, video review of your sessions, unlimited messaging."
          required
        />

        <label htmlFor="plan-price">Price per month</label>
        <input
          id="plan-price"
          type="number"
          min="1"
          step="0.01"
          value={draft.price}
          onChange={(e) => setDraft({ ...draft, price: e.target.value })}
          required
        />

        {/* The coach's say over who moves onto what (27 Sep debug notes item 12). On by default for a
            new plan (decided 5 Oct 2026); an existing plan keeps whatever it was saved with. */}
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={draft.requiresApproval}
            onChange={(e) => setDraft({ ...draft, requiresApproval: e.target.checked })}
          />
          Families need your approval to switch to this plan
        </label>
        {!draft.id && (
          <p className="form-hint">
            A new plan isn&rsquo;t offered to anyone yet. Add it to each athlete who should see it, on their
            Billing tab.
          </p>
        )}

        <div className="detail-actions">
          <button type="submit" disabled={savePlan.isPending}>
            {savePlan.isPending ? "Saving…" : draft.id ? "Save changes" : "Add plan"}
          </button>
          {draft.id && (
            <button type="button" className="chip" onClick={() => setDraft(BLANK)}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <h2>Family discount</h2>
      <p className="form-hint">
        How much cheaper each <em>additional</em> athlete in the same family is, on every plan. The first
        athlete always pays full price.
      </p>
      {saveDiscount.isError && <ErrorState error={saveDiscount.error} />}
      <div className="exercise-row">
        <input
          aria-label="Sibling discount percent"
          type="number"
          min="0"
          max="90"
          step="1"
          value={discountValue}
          onChange={(e) => setDiscount(e.target.value)}
        />
        <button
          type="button"
          disabled={saveDiscount.isPending || Number(discountValue) === siblingDiscountPercent}
          onClick={() => saveDiscount.mutate(Math.round(Number(discountValue)))}
        >
          {saveDiscount.isPending ? "Saving…" : "Save discount"}
        </button>
      </div>
    </>
  );
}
